import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useRef } from 'react';
import { shallowEqual } from 'react-redux';
import { ErrorLoad, Interpolate } from '@wg/wows-react-uikit';

import { CLANSTARS_REWARD_STATUSES } from '~/constants';
import { t } from '~/helpers/localization';
import { useAppDispatch, useAppSelector } from '~/store';
import { claimRewardThunk, sendClaimRewardErrorNotification } from '~/store/thunks/clanStarsThunks';
import { fetchMembers } from '~/Actions/ActionClanProfile';

import styles from './CStarsProgress.scss';
import ProgressBar from './ProgressBar/ProgressBar';

import type { RootState } from '~/store';

const stateSelector = (state: RootState) => {
  return {
    accountClanstars: state.ReducerClanStars.accountClanstars,
    accountRewards: state.ReducerClanStars.accountRewards,
    clanId: state.currentAccount.clanId,
    rewardsInfo: state.ReducerClanStars.rewardsInfo,
  };
};

const CLAIM_REWARD_MAX_RETRIES = 5;
const CLAIM_REWARD_RETRY_DELAY_MS = 1000;

const CStarsProgress: React.FC = () => {
  const { accountClanstars, accountRewards, clanId, rewardsInfo } = useAppSelector(stateSelector, shallowEqual);
  const dispatch = useAppDispatch();

  const retryFetchMembers = React.useCallback(() => {
    if (clanId) {
      void dispatch(fetchMembers(clanId, true));
    }
  }, [clanId, dispatch]);

  const retryCountRef = useRef(0);

  useEffect(() => {
    const hasPendingRewards = accountRewards.some((reward) => reward.status === CLANSTARS_REWARD_STATUSES.PENDING);

    if (!hasPendingRewards) {
      retryCountRef.current = 0;
      return;
    }

    if (retryCountRef.current >= CLAIM_REWARD_MAX_RETRIES) {
      sendClaimRewardErrorNotification();
      return;
    }

    retryCountRef.current += 1;

    const timerId = setTimeout(() => {
      void dispatch(claimRewardThunk());
    }, CLAIM_REWARD_RETRY_DELAY_MS * retryCountRef.current);

    return () => clearTimeout(timerId);
  }, [accountRewards, dispatch]);

  return (
    <div>
      <h2 className={styles.title}>{t('Получайте награды')}</h2>
      <h3 className={styles.text}>
        <Interpolate
          str={t('За заработанные звёзды вы будете получать награды. Звёзд получено: %(stars)s')}
          stars={<span className={styles.star}>{accountClanstars.length}</span>}
          parentTag={'span'}
        />
      </h3>
      {isEmpty(rewardsInfo) ? (
        <ErrorLoad
          isFlat
          key="error-load"
          message={t('Произошла ошибка. Повторите попытку позже')}
          onReloadClick={retryFetchMembers}
        />
      ) : (
        <ProgressBar
          accountRewards={accountRewards}
          clanstarsCount={accountClanstars.length}
          rewardsInfo={rewardsInfo}
        />
      )}
    </div>
  );
};

export default React.memo(CStarsProgress);
